import { DateTime } from 'luxon'
import { FunctionComponent, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Button from '~/components/Button'
import { IllustrationMessage } from '~/components/IllustrationMessage'
import { BaseInputElement } from '~/components/Input'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { PageContent } from '~/components/PageContent'
import { Pagination } from '~/components/Pagination'
import Table, { TableColumns } from '~/components/table/Table'
import TableCell from '~/components/table/TableCell'
import TableRow from '~/components/table/TableRow'
import { Tooltip } from '~/components/Tooltip'
import { UnarchiveConfirmationModal } from '~/feature-components/UnarchiveConfirmationModal'
import { useClientPagination } from '~/hooks/useClientPagination'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'

/** The little every rule kind has in common — enough to list one and bring it back. */
export type ArchivedRuleRow = {
  id: string
  name: string
  updatedAt?: Date
  updatedBy?: string
}

type Props = {
  rules: ArchivedRuleRow[]
  isLoading: boolean
  /** The kind, lower case and used mid-sentence — `floor price rule`, `bidder rule`. */
  noun: string
  /** Where the back button goes, and what it says. */
  backLabel: string
  backPath: string
  onUnarchive: (rule: ArchivedRuleRow) => void | Promise<unknown>
  isUnarchiving: boolean
  /** Owners only. A reader who cannot restore still gets the list, without the column of dead buttons. */
  canUnarchive: boolean
  backTrackingLabel: TrackingLabel
  /**
   * The row's own Unarchive label. The confirmation's two controls extend it rather than restate it, so
   * the modal stays attached to the click that opened it in the funnel — which is why this is a complete
   * label and not a bare prefix.
   */
  unarchiveTrackingLabel: TrackingLabel
}

const COLUMN_WIDTHS = { archivedOn: '220px', archivedBy: '220px', actions: '120px' }

/**
 * The archived rules of one kind, on their own page rather than as a group at the bottom of the list.
 *
 * One component for all four kinds: an archived rule is a name, a date and a way back, and the four
 * lists differ only in what they call themselves. The live lists stay separate because their columns
 * are genuinely different — a floor has a price, a refresh has a timer — but there is nothing left of
 * that here.
 *
 * Reaching this page is owner-only, enforced by the route and again by the API, which refuses the
 * `archived` listing to anyone below owner.
 *
 * Filtered and paged in the browser: the endpoint answers with the whole archive, which is small by
 * construction — a rule only reaches it while nothing uses it.
 */
export const ArchivedRulesPage: FunctionComponent<Props> = ({ rules, isLoading, noun, backLabel, backPath, onUnarchive, isUnarchiving, canUnarchive, backTrackingLabel, unarchiveTrackingLabel }) => {
  const navigate = useNavigate()
  const breadcrumbs = useBreadcrumbs()
  const modal = useGlobalModal()
  const [search, setSearch] = useState('')

  const matching = rules.filter((rule) => rule.name.toLowerCase().includes(search.toLowerCase()))
  const { pageItems, paginationProps, resetPage } = useClientPagination(matching)

  const columns: TableColumns<ArchivedRuleRow> = [
    { name: 'Rule', isSortable: false },
    { name: 'Archived on', isSortable: false, width: COLUMN_WIDTHS.archivedOn },
    { name: 'Archived by', isSortable: false, width: COLUMN_WIDTHS.archivedBy },
    { name: 'Actions', isSortable: false, width: COLUMN_WIDTHS.actions },
  ]

  return (
    <PageContent
      breadcrumbs={breadcrumbs}
      headerActions={
        <Button variant={'tertiary'} iconName={'chevron_left'} onClick={() => navigate(backPath)} trackingLabel={backTrackingLabel}>
          {backLabel}
        </Button>
      }
    >
      <div className={'flex flex-col gap-4'}>
        <div className={'flex items-center gap-4'}>
          <BaseInputElement
            type={'text'}
            iconLeft={'search'}
            labelIsPlaceholder
            label={`Search a ${noun}`}
            value={search}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setSearch(event.target.value)
              // A new query has no page 2 yet, and staying on one would show an empty table.
              resetPage()
            }}
          />
        </div>

        {/* Without a search the total is the whole archive, so an empty one is genuinely empty rather
            than filtered — which is a different thing to tell the reader. */}
        {!isLoading && rules.length === 0 ? (
          <IllustrationMessage iconName={'night_sky'} iconSize={'190px'} title={''} message={`No archived ${noun}.`} />
        ) : (
          <>
            <Table columns={columns} isLoading={isLoading} noWrapHeader>
              {pageItems.length > 0 ? (
                pageItems.map((rule) => (
                  <TableRow key={rule.id} archived>
                    <TableCell className={'break-words'}>{rule.name}</TableCell>
                    <TableCell>{rule.updatedAt ? DateTime.fromISO(rule.updatedAt.toString()).toFormat('dd LLL. yyyy, HH:mm') : '-'}</TableCell>
                    <TableCell>{rule.updatedBy ?? '-'}</TableCell>
                    <TableCell>
                      {canUnarchive && (
                        <Tooltip title={'Unarchive'}>
                          <Button
                            iconName={isUnarchiving ? 'pubstack' : 'unarchive'}
                            iconSpin={isUnarchiving}
                            variant={'tertiary'}
                            disabled={isUnarchiving}
                            title={'Unarchive'}
                            onClick={() =>
                              modal.open(UnarchiveConfirmationModal, {
                                name: rule.name,
                                noun,
                                consequence: `It will be offered again when a stack picks a ${noun}. No stack is changed by restoring it.`,
                                onUnarchive: () => onUnarchive(rule),
                                isPending: isUnarchiving,
                                trackingPrefix: unarchiveTrackingLabel,
                              })
                            }
                            trackingLabel={unarchiveTrackingLabel}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colspan={columns.length}>No results</TableCell>
                </TableRow>
              )}
            </Table>
            <Pagination {...paginationProps} className={'mt-4'} />
          </>
        )}
      </div>
    </PageContent>
  )
}
